import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

const COLORS = [
  '#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8',
  '#82ca9d', '#ffc658', '#ff7300', '#ff5868', '#7785ff'
];

const PortfolioVisualization = ({ data, selectedPath, onPathChange, symbolCache }) => {
  const navigateToPath = (pathParts) => {
    let current = {...data};

    // Remove empty parts and special keywords
    const cleanParts = pathParts.filter(part => part && !['subCategories', 'assets'].includes(part));
    
    if (cleanParts.length === 0) return current;

    // İlk seviye kategori için direkt erişim
    const mainCategory = cleanParts[0];
    if (!current[mainCategory]) return null;
    current = current[mainCategory];

    // Alt kategoriler için
    if (cleanParts.length > 1) {
      const subCategory = cleanParts[1];
      if (current.subCategories?.[subCategory]) {
        current = current.subCategories[subCategory];
      } else if (current.assets?.[subCategory]) {
        current = current.assets[subCategory];
      }
    }

    return current;
  };

  const getDataAtPath = (pathString) => {
    // Root level
    if (!pathString) {
      return Object.entries(data)
        .filter(([key, value]) => 
          !['TRY.TRY.CURRENCY', 'VIST.USD.NYSE'].includes(key) && 
          value.allocation > 0
        )
        .map(([key, value]) => ({
          name: key,
          value: value.allocation || 0,
          path: key,
          children: (value.subCategories && Object.keys(value.subCategories).length > 0) ||
                   (value.assets && Object.keys(value.assets).length > 0)
        }));
    }

    const parts = pathString.split('|');
    const currentData = navigateToPath(parts);
    
    if (!currentData) {
      return [];
    }

    // Alt kategoriler varsa
    if (currentData.subCategories && Object.keys(currentData.subCategories).length > 0) {
      return Object.entries(currentData.subCategories)
        .filter(([_, value]) => value.allocation > 0)
        .map(([key, value]) => ({
          name: key,
          value: value.allocation || 0,
          path: `${pathString}|${key}`,
          children: value.assets && Object.keys(value.assets).length > 0
        }));
    }

    // Asset'ler varsa
    if (currentData.assets && Object.keys(currentData.assets).length > 0) {
      return Object.entries(currentData.assets)
        .filter(([_, value]) => value.allocation > 0)
        .map(([key, value]) => ({
          name: key,
          value: value.allocation || 0,
          path: `${pathString}|assets|${key}`,
          isAsset: true,
          description: symbolCache?.get(key)?.description
        }));
    }

    return [];
  };

  const goBack = () => {
    const parts = selectedPath.split('|');
    const cleanParts = parts.filter(part => part && !['subCategories', 'assets'].includes(part));
    if (cleanParts.length > 0) {
      cleanParts.pop();
    }
    onPathChange(cleanParts.join('|'));
  };

  const getTitle = () => {
    if (!selectedPath) return "Ana Kategoriler";
    const parts = selectedPath.split('|');
    const cleanParts = parts.filter(part => part && !['subCategories', 'assets'].includes(part));
    return `${cleanParts[cleanParts.length - 1] || ''} Dağılımı`;
  };

  const chartData = getDataAtPath(selectedPath);

  if (!chartData || chartData.length === 0) {
    return null;
  }

  const total = chartData.reduce((sum, item) => sum + item.value, 0);
  if (total === 0) return null;

  const handlePieClick = (data) => {
    if (data.children) {
      onPathChange(data.path);
    }
  };

  return (
    <div className="mt-6 border rounded-lg p-4 bg-white">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium">{getTitle()}</h3>
        {selectedPath && (
          <button 
            onClick={goBack}
            className="px-3 py-1 text-sm bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100"
          >
            ← Geri
          </button>
        )}
      </div>
      <div className="h-96">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              labelLine={true}
              label={({ name, percent }) => `${name} (${(percent * 100).toFixed(1)}%)`}
              outerRadius={150}
              fill="#8884d8"
              dataKey="value"
              onClick={(data) => handlePieClick(data.payload)}
              cursor={chartData.some(d => d.children) ? "pointer" : "default"}
            >
              {chartData.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={COLORS[index % COLORS.length]}
                  style={{
                    filter: entry.children ? 'brightness(1)' : 'brightness(0.95)',
                    transition: 'filter 0.3s'
                  }}
                  className={entry.children ? "hover:brightness-90" : ""}
                />
              ))}
            </Pie>
            <Tooltip 
              content={({ payload }) => {
                if (!payload || !payload.length) return null;
                const data = payload[0].payload;
                const symbol = data.symbol || data.name.split('.')[0];
                
                return (
                  <div className="bg-white p-3 border rounded shadow min-w-[200px]">
                    <div className="flex items-start gap-2 mb-2">
                      {!data.children && (
                        <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center bg-gray-100 rounded">
                          <img 
                            src={`https://storage.googleapis.com/iex/api/logos/${symbol}.png`}
                            alt={symbol}
                            className="w-6 h-6 object-contain"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiPjxwYXRoIGQ9Ik0yMSA5LjY2N3YtMS42NjdBMiAyIDAgMCAwIDE5IDZINWEyIDIgMCAwIDAtMiAydjEuNjY3Ii8+PHBhdGggZD0iTTMgMTQuMzMzVjE2YTIgMiAwIDAgMCAyIDJoMTRhMiAyIDAgMCAwIDItMnYtMS42NjciLz48cGF0aCBkPSJNMyAxMGg2djRIM3ptMTIgMGg2djRoLTZ6Ii8+PC9zdmc+';
                            }}
                          />
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="font-medium flex items-center gap-1">
                          <span>{symbol}</span>
                          {!data.children && data.name.includes('.') && (
                            <span className="text-xs text-gray-500">
                              ({data.name.split('.')[1]})
                            </span>
                          )}
                        </div>
                        {!data.children && data.description && (
                          <p className="text-sm text-gray-600 mt-0.5 line-clamp-2">{data.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="pt-1 border-t">
                      <p className="text-sm">Allocation: <span className="font-medium">{data.value}%</span></p>
                    </div>
                    {data.children && (
                      <p className="text-xs text-blue-600 mt-2 pt-1 border-t">
                        Alt kategorileri görmek için tıklayın
                      </p>
                    )}
                  </div>
                );
              }}
            />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-4 text-sm text-gray-500">
        {chartData.some(d => d.children) ? 
          "* Detaylar için dilimlere tıklayabilirsiniz" : 
          ""}
      </div>
    </div>
  );
};

export default PortfolioVisualization;